// Fix for the useDashboardData.tsx hook

import { useState, useEffect, useCallback, useRef } from 'react';
import { ethers } from 'ethers';
import { BalanceInfo, DashboardDataFetchResult, TokenBalance } from '../types/DashboardTypes';
import { MultiAssetBalanceFetcher, AssetBalance } from '../utils/multiAssetBalanceFetcher';
import logger from '../utils/logger';

// Debug logging configuration - environment aware
const logDebug = (message: string, data?: any) => {
    if (process.env.NODE_ENV === 'development') {
        if (data) {
            // logger.log('[DashboardData]', message, data);
        } else {
            // logger.log('[DashboardData]', message);
        }
    }
};

// Configuration for timeouts and retries
const CONFIG = {
    BALANCE_FETCH_TIMEOUT: 5000,     // 5 seconds
    MAX_CONCURRENT_REQUESTS: 3,      // Limit concurrent balance fetches
    CACHE_EXPIRATION: 5 * 60 * 1000, // 5 minutes
    DEBOUNCE_MS: 500,                // Debounce time for repeated calls
    MAX_RETRIES: 2,                  // Maximum number of retries for failed requests
    RETRY_DELAY_MS: 1000,            // Base delay between retries (increases exponentially)
};

interface DashboardDataProps {
    provider: ethers.providers.Web3Provider | null;
    deploymentDetails: any;
    contracts: any[];
    memerCoreAddress?: string;
}

// Type guard to verify if an object is a valid BalanceInfo
function isBalanceInfo(obj: any): obj is BalanceInfo {
    return obj !== null &&
        typeof obj.name === 'string' &&
        typeof obj.address === 'string' &&
        typeof obj.balance === 'string' &&
        (obj.type === 'multiSig' || obj.type === 'contract' || obj.type === 'token');
}

// Simple debounce implementation that works with async functions
function debounce<T extends (...args: any[]) => Promise<any>>(
    func: T,
    wait: number
): (...args: Parameters<T>) => Promise<ReturnType<T>> {
    let timeout: NodeJS.Timeout | null = null;

    return async function (...args: Parameters<T>): Promise<ReturnType<T>> {
        return new Promise((resolve, reject) => {
            if (timeout) {
                clearTimeout(timeout);
            }

            timeout = setTimeout(async () => {
                timeout = null;
                try {
                    const result = await func(...args);
                    resolve(result as ReturnType<T>);
                } catch (error) {
                    reject(error);
                }
            }, wait);
        });
    };
}

// Utility function for retry logic
const fetchWithRetry = async <T,>(fn: () => Promise<T>, maxRetries = CONFIG.MAX_RETRIES): Promise<T> => {
    let retries = 0;
    while (retries <= maxRetries) {
        try {
            return await fn();
        } catch (error) {
            retries++;
            if (retries > maxRetries) throw error;
            // Exponential backoff
            const delay = CONFIG.RETRY_DELAY_MS * Math.pow(2, retries - 1);
            logDebug(`Retry ${retries}/${maxRetries} after ${delay}ms`);
            await new Promise(r => setTimeout(r, delay));
        }
    }
    throw new Error('Max retries exceeded');
};

export const useDashboardData = ({
    provider,
    deploymentDetails = {},
    contracts = [],
    memerCoreAddress
}: DashboardDataProps): DashboardDataFetchResult & { refreshData: () => Promise<DashboardDataFetchResult | null> } => {

    const [dashboardData, setDashboardData] = useState<DashboardDataFetchResult>({
        multiSigBalances: [],
        contractBalances: [],
        isLoading: true,
        lastUpdated: null
    });

    // Use a ref to maintain stable references to functions that will be debounced
    const functionRefs = useRef({
        // Will be initialized in useEffect
        fetchTokenBalance: async (_p: any, _a: string, _t: string): Promise<TokenBalance[]> => {
            return [{ token: 'MEMER', balance: '0' }];
        }
    });

    // Enhanced check for provider connection that works with both MetaMask and Coinbase Wallet
    const isProviderConnected = useCallback(() => {
        // Basic provider check
        if (!provider) return false;

        // Log provider properties for debugging
        console.log("Provider properties:", Object.keys(provider));

        // More flexible check that works with different wallet implementations
        const hasGetSigner = typeof provider.getSigner === 'function';
        const hasGetNetwork = typeof provider.getNetwork === 'function';
        const hasRequest = typeof (provider as any).request === 'function';
        const hasEthereumRequest = window.ethereum && typeof (window.ethereum as any).request === 'function';

        // Log connection details
        console.log("Provider connection check:", {
            hasGetSigner,
            hasGetNetwork,
            hasRequest,
            hasEthereumRequest
        });

        // Consider connected if either the provider has the expected methods OR window.ethereum is available
        return (hasGetSigner && hasGetNetwork) || hasRequest || hasEthereumRequest;
    }, [provider]);

    // Check if a contract is a MultiSig wallet
    const isMultiSigWallet = useCallback((contract: any): boolean => {
        // Skip check if contract is invalid
        if (!contract || !contract.name) {
            return false;
        }

        // Check by name
        const multiSigNames = [
            'multisig', 'wallet', 'treasury', 'founder',
            'charity', 'marketing', 'safe', 'community', 'reward'
        ];
        const hasMultiSigName = multiSigNames.some(name =>
            contract.name.toLowerCase().includes(name)
        );

        // Check by ABI (if available)
        let hasMultiSigMethods = false;
        if (contract.abi && Array.isArray(contract.abi)) {
            hasMultiSigMethods = contract.abi.some((item: any) =>
                item && item.type === 'function' &&
                ['getOwners', 'execTransaction', 'submitTransaction', 'confirmTransaction'].includes(item.name)
            );
        }

        // Enhanced logging
        if (hasMultiSigName || hasMultiSigMethods) {
            logDebug(`MultiSig Detected: ${contract.name}`, {
                hasMultiSigName,
                hasMultiSigMethods
            });
        }

        return hasMultiSigName || hasMultiSigMethods;
    }, []);

    // Setup function implementations in an effect to avoid recreating them on every render
    useEffect(() => {
        // Actual implementation of fetchTokenBalance
        functionRefs.current.fetchTokenBalance = async (
            provider: ethers.providers.Web3Provider,
            address: string,
            tokenAddress: string
        ): Promise<TokenBalance[]> => {
            if (!provider || !tokenAddress || !address) {
                logDebug('Missing provider, token address, or wallet address');
                return [{ token: 'MEMER', balance: '0' }];
            }

            try {
                // Add more detailed logging
                // logger.log(`FETCHING TOKEN BALANCE FOR: Address: ${address.substring(0, 10)}... Token: ${tokenAddress.substring(0, 10)}...`);

                // Create token contract with full ABI for better debugging
                const tokenContract = new ethers.Contract(
                    tokenAddress,
                    [
                        'function balanceOf(address) view returns (uint256)',
                        'function decimals() view returns (uint8)',
                        'function symbol() view returns (string)'
                    ],
                    provider
                );

                // Get token symbol and decimals (with fallbacks)
                let symbol = 'MEMER';
                let decimals = 18;

                try {
                    symbol = await tokenContract.symbol();
                    decimals = await tokenContract.decimals();
                    // logger.log(`Token info: Symbol ${symbol}, Decimals ${decimals}`);
                } catch (e) {
                    // logger.log('Error fetching token metadata, using defaults');
                }

                // Implement timeout for balance fetch
                const balancePromise = tokenContract.balanceOf(address);
                const timeoutPromise = new Promise<never>((_, reject) =>
                    setTimeout(() => reject(new Error('Token balance fetch timeout')), CONFIG.BALANCE_FETCH_TIMEOUT)
                );

                // Race the promises
                const rawBalance = await Promise.race([balancePromise, timeoutPromise]) as ethers.BigNumber;

                // Log the raw balance for debugging
                // logger.log(`Raw balance for ${address.substring(0, 10)}...: ${rawBalance.toString()}`);

                // Format balance
                const formattedBalance = ethers.utils.formatUnits(rawBalance, decimals);
                // logger.log(`Formatted balance: ${formattedBalance} ${symbol}`);

                return [{ token: symbol, balance: formattedBalance }];
            } catch (error) {
                logger.error(`Error fetching token balance for ${address}:`, error);
                return [{ token: 'MEMER', balance: '0' }];
            }
        };
    }, []);

    // Create a stable reference to the debounced function
    const debouncedFetchTokenBalance = useCallback(
        async (
            provider: ethers.providers.Web3Provider,
            address: string,
            tokenAddress: string
        ): Promise<TokenBalance[]> => {
            try {
                // Use the function ref implementation, which is stable
                return await fetchWithRetry(() =>
                    functionRefs.current.fetchTokenBalance(provider, address, tokenAddress)
                );
            } catch (error) {
                logger.error(`Error in debounced token balance fetch for ${address}:`, error);
                // Always return a valid array with default values on error
                return [{ token: 'MEMER', balance: '0' }];
            }
        },
        [] // Empty dependency array to keep it stable
    );

    // Batch fetch balances to reduce network load - with improved error handling
    const batchFetchTokenBalances = useCallback(async (
        provider: ethers.providers.Web3Provider,
        addresses: string[],
        tokenAddress: string
    ): Promise<Record<string, TokenBalance[]>> => {
        if (!provider || !tokenAddress || addresses.length === 0) {
            // logDebug('Missing provider, token address, or addresses');
            return {};
        }

        // logDebug(`Batch fetching token balances for ${addresses.length} addresses`);
        const results: Record<string, TokenBalance[]> = {};

        try {
            // Process in smaller batches to not overwhelm the network
            const batchSize = CONFIG.MAX_CONCURRENT_REQUESTS;

            for (let i = 0; i < addresses.length; i += batchSize) {
                const batch = addresses.slice(i, i + batchSize);
                const batchPromises = batch.map(address => {
                    return debouncedFetchTokenBalance(provider, address, tokenAddress)
                        .then(balances => {
                            results[address] = balances;
                            return balances;
                        })
                        .catch(error => {
                            logger.error(`Error fetching token balance for ${address}:`, error);
                            results[address] = [{ token: 'MEMER', balance: '0' }];
                            return [{ token: 'MEMER', balance: '0' }];
                        });
                });

                await Promise.all(batchPromises);

                // Add a small delay between batches to avoid rate limiting
                if (i + batchSize < addresses.length) {
                    await new Promise(resolve => setTimeout(resolve, 300));
                }
            }

            return results;
        } catch (error) {
            // logDebug(`Error in batch token balance fetch: ${error}`);

            // Fallback: attempt to fetch individually for each address
            for (const address of addresses) {
                try {
                    const balances = await debouncedFetchTokenBalance(provider, address, tokenAddress);
                    results[address] = balances;
                } catch (fallbackError) {
                    results[address] = [{ token: 'MEMER', balance: '0' }];
                }
            }

            return results;
        }
    }, [debouncedFetchTokenBalance]);

    // Helper function to get contract category
    const getCategoryForContract = useCallback((name: string): 'core' | 'proxy' | 'multisig' | 'utility' | 'other' => {
        const lowerName = name.toLowerCase();

        if (lowerName.includes('features') || lowerName.includes('controller')) {
            return 'proxy';
        }
        if (lowerName.includes('core') || lowerName.includes('multisigfactory') ||
            lowerName.includes('timelockcontroller')) {
            return 'core';
        }
        if (lowerName.includes('multisig') || lowerName.includes('treasury') ||
            lowerName.includes('founder') || lowerName.includes('charity') ||
            lowerName.includes('marketing')) {
            return 'multisig';
        }
        if (lowerName.includes('bridge') || lowerName.includes('helper')) {
            return 'utility';
        }
        return 'other';
    }, []);

    // Main data fetching function with improved error handling and wallet connection checking
    const fetchDashboardData = useCallback(async (): Promise<DashboardDataFetchResult | null> => {
        // logDebug('Starting dashboard data fetch');

        // logger.log('DASHBOARD DATA DEBUG:',
        //     'Provider ready:', !!provider,
        //     'Contracts:', contracts.length,
        //     'MemerCoreAddress:', memerCoreAddress
        // );

        // Check if provider is properly connected
        if (!isProviderConnected()) {
            // logDebug('No connected provider available');
            setDashboardData(prev => ({
                ...prev,
                isLoading: false,
                error: 'Please connect your wallet to view dashboard data',
                needsConnection: true
            }));
            return null;
        }

        if (!deploymentDetails || Object.keys(deploymentDetails).length === 0) {
            // logDebug('No deployment details available');
            setDashboardData(prev => ({
                ...prev,
                isLoading: false,
                error: 'No deployment details available'
            }));
            return null;
        }

        // logDebug(`Provider ready, found ${contracts.length} contracts, memerCoreAddress: ${memerCoreAddress || 'Not set'}`);

        try {
            // Update loading state
            setDashboardData(prev => ({
                ...prev,
                isLoading: true,
                error: undefined,
                needsConnection: false
            }));

            // Initialize result arrays
            const multiSigBalances: BalanceInfo[] = [];
            const contractBalances: BalanceInfo[] = [];

            // Separate contracts by type (multisig vs others)
            const multiSigContracts = contracts.filter(contract =>
                contract.type === 'multisig' || isMultiSigWallet(contract)
            );

            const otherContracts = contracts.filter(contract =>
                contract.type !== 'multisig' && !isMultiSigWallet(contract)
            );

            // logDebug(`Found ${multiSigContracts.length} MultiSig contracts and ${otherContracts.length} other contracts`);

            // Get all addresses we need to fetch balances for
            const allAddresses = [
                ...multiSigContracts.map(c => c.address),
                ...otherContracts.map(c => c.address)
            ];

            // Use the new multi-asset balance fetcher
            logger.log(`[DashboardData] Fetching multi-asset balances for ${allAddresses.length} addresses`);

            const multiAssetFetcher = new MultiAssetBalanceFetcher(provider!, deploymentDetails);

            // Prepare contracts for batch fetching
            const contractsForFetching = [
                ...multiSigContracts.map(c => ({ name: c.name, address: c.address })),
                ...otherContracts.map(c => ({ name: c.name, address: c.address }))
            ];

            // Batch fetch all asset balances
            const allAssetBalances = await multiAssetFetcher.batchFetchAssetBalances(contractsForFetching);

            logger.log(`[DashboardData] Multi-asset balances fetched for ${Object.keys(allAssetBalances).length} contracts`);

            // Helper function to convert AssetBalance[] to legacy format
            const convertAssetBalancesToLegacy = (assetBalances: AssetBalance[]): { ethBalance: string; tokenBalances: TokenBalance[] } => {
                let ethBalance = '0';
                const tokenBalances: TokenBalance[] = [];

                for (const asset of assetBalances) {
                    if (asset.type === 'ETH') {
                        ethBalance = asset.balance;
                    } else if (asset.type === 'WETH') {
                        // For pools, show WETH as ETH balance
                        ethBalance = asset.balance;
                    } else if (asset.type === 'TOKEN') {
                        tokenBalances.push({
                            token: asset.symbol,
                            balance: asset.balance
                        });
                    } else if (asset.type === 'NFT') {
                        tokenBalances.push({
                            token: asset.symbol,
                            balance: asset.balance
                        });
                    } else if (asset.type === 'LP') {
                        tokenBalances.push({
                            token: asset.symbol,
                            balance: asset.balance
                        });
                    }
                }

                return { ethBalance, tokenBalances };
            };

            // Process MultiSig wallets
            for (const contract of multiSigContracts) {
                if (!contract.address || !ethers.utils.isAddress(contract.address)) continue;

                // Get asset balances for this contract
                const assetBalances = allAssetBalances[contract.address] || [];
                const { ethBalance, tokenBalances } = convertAssetBalancesToLegacy(assetBalances);

                const balanceInfo: BalanceInfo = {
                    name: contract.name,
                    address: contract.address,
                    balance: ethBalance,
                    type: 'multiSig',
                    tokenBalances
                };

                multiSigBalances.push(balanceInfo);
            }

            // Process other contracts
            for (const contract of otherContracts) {
                if (!contract.address || !ethers.utils.isAddress(contract.address)) continue;

                // Get asset balances for this contract
                const assetBalances = allAssetBalances[contract.address] || [];
                const { ethBalance, tokenBalances } = convertAssetBalancesToLegacy(assetBalances);

                const balanceInfo: BalanceInfo = {
                    name: contract.name,
                    address: contract.address,
                    balance: ethBalance,
                    type: 'contract',
                    tokenBalances
                };

                contractBalances.push(balanceInfo);
            }

            // logDebug(`Processed ${multiSigBalances.length} MultiSig wallets and ${contractBalances.length} contracts`);

            // Create placeholder data if needed (development/testing)
            if (multiSigBalances.length === 0 && process.env.NODE_ENV === 'development') {
                // logDebug('Adding placeholder MultiSig data for development');
                multiSigBalances.push({
                    name: "Placeholder MultiSig",
                    address: "0x0000000000000000000000000000000000000001",
                    balance: "1.234",
                    type: 'multiSig',
                    tokenBalances: [{ token: 'MEMER', balance: '1000000.0' }]
                });
            }

            if (contractBalances.length === 0 && process.env.NODE_ENV === 'development') {
                // logDebug('Adding placeholder contract data for development');
                contractBalances.push({
                    name: "Placeholder Contract",
                    address: "0x0000000000000000000000000000000000000002",
                    balance: "0.5",
                    type: 'contract',
                    tokenBalances: []
                });
            }

            // Prepare final dashboard data
            const updatedData: DashboardDataFetchResult = {
                multiSigBalances,
                contractBalances,
                isLoading: false,
                lastUpdated: new Date().toISOString()
            };

            // (`Dashboard data fetch complete: ${multiSigBalances.length} MultiSigs, ${contractBalances.length} contracts`);

            // Update state
            setDashboardData(updatedData);

            // Save to localStorage for potential offline/cache use
            try {
                localStorage.setItem('dashboardBalances', JSON.stringify({
                    data: updatedData,
                    timestamp: Date.now()
                }));
            } catch (e) {
                // logDebug('Error saving dashboard data to localStorage:', e);
            }

            // Return the updated data
            return updatedData;
        } catch (error) {
            logger.error('Error fetching dashboard data:', error);

            // Update state with error
            setDashboardData(prev => ({
                ...prev,
                isLoading: false,
                error: `Failed to fetch dashboard data: ${error instanceof Error ? error.message : 'Unknown error'}`
            }));

            return null;
        }
    }, [
        provider,
        deploymentDetails,
        contracts,
        memerCoreAddress,
        batchFetchTokenBalances,
        isMultiSigWallet,
        getCategoryForContract,
        isProviderConnected
    ]);

    // Create a manually debounced version of fetchDashboardData
    const debouncedFetchRef = useRef<{
        timer: NodeJS.Timeout | null;
        fetch: () => Promise<void>;
    }>({
        timer: null,
        fetch: async () => { } // Will be initialized below
    });

    // Initialize the debounced fetch function
    useEffect(() => {
        debouncedFetchRef.current.fetch = async () => {
            if (debouncedFetchRef.current.timer) {
                clearTimeout(debouncedFetchRef.current.timer);
            }

            debouncedFetchRef.current.timer = setTimeout(async () => {
                debouncedFetchRef.current.timer = null;
                await fetchDashboardData();
            }, 300);
        };
    }, [fetchDashboardData]);

    // Initialize data from cache if available
    useEffect(() => {
        const initializeData = async () => {
            // First check if provider is connected
            if (!isProviderConnected()) {
                // logDebug('Provider not connected, setting needsConnection state');
                setDashboardData(prev => ({
                    ...prev,
                    isLoading: false,
                    needsConnection: true,
                    error: 'Please connect your wallet to view dashboard data'
                }));
                return;
            }

            try {
                const cachedData = localStorage.getItem('dashboardBalances');
                if (cachedData) {
                    const { data, timestamp } = JSON.parse(cachedData);
                    const isExpired = Date.now() - timestamp > CONFIG.CACHE_EXPIRATION;

                    if (!isExpired) {
                        //logDebug('Using cached dashboard data');
                        setDashboardData({
                            ...data,
                            isLoading: false,
                            needsConnection: false
                        });

                        // Still refresh in the background after a short delay
                        setTimeout(() => {
                            debouncedFetchRef.current.fetch();
                        }, 2000);
                    } else {
                        // logDebug('Cached dashboard data expired, fetching fresh data');
                        fetchDashboardData();
                    }
                } else {
                    // logDebug('No cached data found, fetching fresh data');
                    fetchDashboardData();
                }
            } catch (error) {
                logger.error('Error loading cached dashboard data:', error);
                fetchDashboardData();
            }
        };

        initializeData();
    }, [fetchDashboardData, isProviderConnected]);

    // Add effect to detect provider connection changes
    useEffect(() => {
        // When provider changes, check connection status
        if (isProviderConnected()) {
            if (dashboardData.needsConnection) {
                // logDebug('Provider connected, refreshing data');
                // Clear the needs connection flag
                setDashboardData(prev => ({
                    ...prev,
                    needsConnection: false
                }));
                fetchDashboardData();
            }
        } else {
            // Set needs connection flag if not already set
            if (!dashboardData.needsConnection) {
                // logDebug('Provider disconnected, updating state');
                setDashboardData(prev => ({
                    ...prev,
                    needsConnection: true,
                    error: 'Please connect your wallet to view dashboard data'
                }));
            }
        }
    }, [provider, fetchDashboardData, dashboardData.needsConnection, isProviderConnected]);

    // Refresh function that can be called manually
    const refreshData = useCallback(async () => {
        // logDebug('Manual refresh requested');
        return await fetchDashboardData();
    }, [fetchDashboardData]);

    // Return both the current state and the refresh function
    return {
        ...dashboardData,
        refreshData
    };
};

export default useDashboardData;
